import { useCallback, useMemo, useRef, useState } from 'react'
import type { TFunction } from 'i18next'
import { useIngestionMutations } from '../../../hooks/use-ingestion-mutations'
import { useIngestionRuns } from '../../../hooks/use-ingestion-runs'
import { useProcessableCollections } from '../../../hooks/use-processable-collections'
import { usePollingRefetch } from '../../../hooks/use-polling-refetch'
import { isActiveIngestionStatus } from '../../../types/ingestion'

interface UseCaseProcessingTabArgs {
  caseId: string
  t: TFunction<'cases'>
}

export interface CaseProcessingTabState {
  collections: ReturnType<typeof useProcessableCollections>['collections']
  collectionsLoading: boolean
  collectionsError: string | null
  runs: ReturnType<typeof useIngestionRuns>['runs']
  runsLoading: boolean
  runsError: string | null
  isRowBusy: (receiptId: string) => boolean
  verifying: boolean
  flagging: boolean
  mutationError: string | null
  selectedRunId: string | null
  setSelectedRunId: (id: string | null) => void
  triageReceiptId: string | null
  openTriage: (receiptId: string) => void
  closeTriage: () => void
  showCorruptDialog: boolean
  corruptReason: string
  setCorruptReason: (v: string) => void
  closeCorruptDialog: () => void
  clearError: () => void
  handleProcess: (receiptId: string) => Promise<void>
  handleVerify: (runId: string) => Promise<void>
  handleFlagCorruptRequest: (runId: string) => void
  handleFlagCorruptConfirm: () => Promise<void>
}

interface FlagCorruptConfirmDeps {
  caseId: string
  corruptRunId: string | null
  corruptReason: string
  setShowCorruptDialog: (v: boolean) => void
  setCorruptRunId: (v: string | null) => void
  setCorruptReason: (v: string) => void
  mutations: ReturnType<typeof useIngestionMutations>
  runsState: ReturnType<typeof useIngestionRuns>
  t: TFunction<'cases'>
}

function useFlagCorruptConfirm(deps: FlagCorruptConfirmDeps): () => Promise<void> {
  const {
    caseId,
    corruptRunId,
    corruptReason,
    setShowCorruptDialog,
    setCorruptRunId,
    setCorruptReason,
    mutations,
    runsState,
    t,
  } = deps
  return useCallback(async () => {
    if (!corruptRunId) return
    if (!globalThis.confirm(t('detail.processing.confirmCorrupt'))) return
    const result = await mutations.flagCorrupt({
      caseId,
      runId: corruptRunId,
      reason: corruptReason,
    })
    if (result) {
      setShowCorruptDialog(false)
      setCorruptRunId(null)
      setCorruptReason('')
      void runsState.refetch()
    }
  }, [
    corruptRunId,
    corruptReason,
    caseId,
    mutations,
    runsState,
    setShowCorruptDialog,
    setCorruptRunId,
    setCorruptReason,
    t,
  ])
}

interface ProcessStartDeps {
  caseId: string
  mutations: ReturnType<typeof useIngestionMutations>
  collectionsState: ReturnType<typeof useProcessableCollections>
  runsState: ReturnType<typeof useIngestionRuns>
  t: TFunction<'cases'>
}

interface ProcessStartResult {
  isRowBusy: (receiptId: string) => boolean
  handleProcess: (receiptId: string) => Promise<void>
}

/**
 * Owns the "Process" start flow: derives which receipts have an in-progress run,
 * polls the two lists while any run is active, and guards against a double-tap
 * starting a duplicate run.
 */
function useProcessStart(deps: ProcessStartDeps): ProcessStartResult {
  const { caseId, mutations, collectionsState, runsState, t } = deps

  // Receipts whose run is still in progress — their Process control must stay
  // disabled (survives a page reload mid-run, unlike a transient in-flight flag).
  const activeReceiptIds = useMemo(
    () =>
      new Set(
        runsState.runs
          .filter((run) => isActiveIngestionStatus(run.status))
          .map((run) => run.collectionReceiptId)
      ),
    [runsState.runs]
  )
  const hasActiveRun = activeReceiptIds.size > 0

  // Poll both lists only while a run is in progress; stops when all are terminal.
  usePollingRefetch(collectionsState.refetch, hasActiveRun)
  usePollingRefetch(runsState.refetch, hasActiveRun)

  // Bridges the click → runs-refetch window so a rapid second tap cannot start a
  // duplicate run before the new run appears in `activeReceiptIds`. The ref is
  // the authoritative, synchronous guard (atomic regardless of render/DOM
  // timing); the mirrored state exists only to re-render the disabled control.
  const startingRef = useRef<Set<string>>(new Set())
  const [startingReceiptIds, setStartingReceiptIds] = useState<ReadonlySet<string>>(
    () => new Set<string>()
  )

  const isRowBusy = useCallback(
    (receiptId: string): boolean =>
      startingReceiptIds.has(receiptId) || activeReceiptIds.has(receiptId),
    [startingReceiptIds, activeReceiptIds]
  )

  const handleProcess = useCallback(
    async (receiptId: string) => {
      // Claim the receipt synchronously before any await/confirm so a second
      // synchronous call cannot slip past the guard.
      if (startingRef.current.has(receiptId) || activeReceiptIds.has(receiptId)) return
      startingRef.current.add(receiptId)
      if (!globalThis.confirm(t('detail.processing.confirmProcess'))) {
        startingRef.current.delete(receiptId)
        return
      }
      setStartingReceiptIds(new Set(startingRef.current))
      try {
        const result = await mutations.startRun(caseId, receiptId)
        if (result) {
          await Promise.all([collectionsState.refetch(), runsState.refetch()])
        }
      } finally {
        startingRef.current.delete(receiptId)
        setStartingReceiptIds(new Set(startingRef.current))
      }
    },
    [activeReceiptIds, caseId, mutations, collectionsState, runsState, t]
  )

  return { isRowBusy, handleProcess }
}

interface TriagePanelResult {
  triageReceiptId: string | null
  openTriage: (receiptId: string) => void
  closeTriage: () => void
}

/**
 * Owns which receipt's artefact-triage panel (if any) is open. Closing
 * refetches the processable-collections list so a completed selective run
 * is reflected without a manual reload.
 */
function useTriagePanel(refetchCollections: () => Promise<void>): TriagePanelResult {
  const [triageReceiptId, setTriageReceiptId] = useState<string | null>(null)
  const openTriage = useCallback((receiptId: string) => {
    setTriageReceiptId(receiptId)
  }, [])
  const closeTriage = useCallback(() => {
    setTriageReceiptId(null)
    void refetchCollections()
  }, [refetchCollections])
  return { triageReceiptId, openTriage, closeTriage }
}

export function useCaseProcessingTab({
  caseId,
  t,
}: UseCaseProcessingTabArgs): CaseProcessingTabState {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [corruptRunId, setCorruptRunId] = useState<string | null>(null)
  const [corruptReason, setCorruptReason] = useState('')
  const [showCorruptDialog, setShowCorruptDialog] = useState(false)

  const collectionsState = useProcessableCollections(caseId)
  const runsState = useIngestionRuns(caseId)
  const mutations = useIngestionMutations()

  const { isRowBusy, handleProcess } = useProcessStart({
    caseId,
    mutations,
    collectionsState,
    runsState,
    t,
  })

  const handleVerify = useCallback(
    async (runId: string) => {
      if (!globalThis.confirm(t('detail.processing.confirmVerify'))) return
      const result = await mutations.verifyRun(caseId, runId)
      if (result) void runsState.refetch()
    },
    [caseId, mutations, runsState, t]
  )

  const handleFlagCorruptRequest = useCallback((runId: string) => {
    setCorruptRunId(runId)
    setCorruptReason('')
    setShowCorruptDialog(true)
  }, [])

  const handleFlagCorruptConfirm = useFlagCorruptConfirm({
    caseId,
    corruptRunId,
    corruptReason,
    setShowCorruptDialog,
    setCorruptRunId,
    setCorruptReason,
    mutations,
    runsState,
    t,
  })

  const closeCorruptDialog = useCallback(() => {
    setShowCorruptDialog(false)
    setCorruptRunId(null)
  }, [])

  const { triageReceiptId, openTriage, closeTriage } = useTriagePanel(collectionsState.refetch)

  return {
    collections: collectionsState.collections,
    collectionsLoading: collectionsState.loading,
    collectionsError: collectionsState.error,
    runs: runsState.runs,
    runsLoading: runsState.loading,
    runsError: runsState.error,
    isRowBusy,
    verifying: mutations.verifying,
    flagging: mutations.flagging,
    mutationError: mutations.error,
    selectedRunId,
    setSelectedRunId,
    triageReceiptId,
    openTriage,
    closeTriage,
    showCorruptDialog,
    corruptReason,
    setCorruptReason,
    closeCorruptDialog,
    clearError: mutations.clearError,
    handleProcess,
    handleVerify,
    handleFlagCorruptRequest,
    handleFlagCorruptConfirm,
  }
}
